/**
 * ChildPlansPage.jsx
 * Child-facing page: all active study plans with Learn and Quiz actions.
 * Reached via /vocab/child/:childId/plans
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, BookOpen, PuzzlePiece, ArrowsClockwise,
} from "@phosphor-icons/react";
import { Mascot }   from "@/components/styleguide/Mascot";
import { cn }       from "@/lib/utils";
import { api }      from "./api";
import { iconConfig } from "./planIcons";

/* ── Plan card ──────────────────────────────────────────────────────────── */
function PlanCard({ plan, childId, helpEnabled }) {
  const navigate = useNavigate();
  const { Icon, bg, text } = iconConfig(plan.icon);
  const { quiz_count } = plan.stats ?? {};
  const wordCount = plan.word_ids.length;

  const helpSuffix = helpEnabled ? "&help=true" : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-soft"
    >
      {/* Card header */}
      <div className={cn("px-5 py-4", bg)}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/70 shadow-sm">
              <Icon size={20} weight="fill" className={text} />
            </span>
            <div>
              <p className="font-display text-lg font-black text-foreground">{plan.name}</p>
              <p className="text-xs font-semibold text-muted-foreground">
                {wordCount} word{wordCount !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          {/* Quiz stat */}
          <div className="text-right">
            <p className="font-display text-2xl font-black text-foreground">
              {quiz_count ?? 0}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              {quiz_count === 1 ? "quiz done" : "quizzes done"}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 px-5 py-4">
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate(`/vocab/child/${childId}/learn?plan_id=${plan.plan_id}${helpEnabled ? "&help=true" : ""}`)}
          className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-primary py-3 font-display text-sm font-black text-white shadow-glow transition hover:brightness-105"
        >
          <BookOpen size={16} weight="fill" />
          Learn
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate(`/vocab/child/${childId}/quiz/mixed?plan_id=${plan.plan_id}${helpSuffix}`)}
          className="flex flex-1 items-center justify-center gap-2 rounded-2xl border-2 border-secondary/40 bg-secondary/10 py-3 font-display text-sm font-black text-secondary-ink transition hover:bg-secondary/20"
        >
          <PuzzlePiece size={16} weight="fill" />
          Quiz · {plan.quiz_count} Qs
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ── Main page ──────────────────────────────────────────────────────────── */
export default function ChildPlansPage() {
  const { childId } = useParams();
  const navigate    = useNavigate();

  const [plans,       setPlans]       = useState(null);
  const [child,       setChild]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const helpEnabled = child?.quiz_help_enabled ?? false;

  useEffect(() => {
    Promise.all([
      api("/children").catch(() => []),
      api(`/children/${childId}/plans/active`).catch(() => []),
    ]).then(([children, plansData]) => {
      setChild(children.find(c => c.child_id === childId) ?? null);
      setPlans(plansData);
    }).finally(() => setLoading(false));
  }, [childId]);

  /* Combined quiz — all active plan words via focus_word_ids */
  function handleTryAll() {
    if (!plans?.length) return;
    const allIds = [...new Set(plans.flatMap(p => p.word_ids))].join(",");
    navigate(`/vocab/child/${childId}/quiz/mixed?focus_word_ids=${allIds}`);
  }

  return (
    <div
      className="relative min-h-[100svh] overflow-hidden"
      style={{ background: "linear-gradient(to bottom, #FFF7EF, #FFF1E6, #DCF3EC)" }}
    >
      {/* Decorative blobs */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute right-[10%] top-[4%] h-20 w-20 rounded-full bg-secondary/30 blur-xl" />
        <div className="absolute left-[6%] top-[40%] h-24 w-24 rounded-full bg-teal/20 blur-2xl" />
      </div>

      {/* Header */}
      <div className="relative z-10 flex items-center gap-3 px-4 py-4 sm:px-6">
        <button
          onClick={() => navigate(`/vocab/child/${childId}`)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-card/85 text-muted-foreground shadow-soft backdrop-blur transition hover:bg-card"
          aria-label="Back to home"
        >
          <ArrowLeft size={16} weight="bold" />
        </button>
        <p className="font-display text-xl font-black text-foreground">My Plans</p>
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-2xl px-4 pb-16 sm:px-6">

        {/* Loading */}
        {loading && (
          <div className="space-y-4">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-36 animate-pulse rounded-[1.75rem] bg-card/60" />
            ))}
          </div>
        )}

        {/* No plans */}
        {!loading && plans?.length === 0 && (
          <div className="flex flex-col items-center gap-3 rounded-3xl bg-card/80 px-6 py-12 text-center shadow-soft backdrop-blur">
            <Mascot size={72} mood="happy" animated={false} />
            <p className="font-display text-xl font-black text-foreground">No plans yet</p>
            <p className="text-sm font-semibold text-muted-foreground">
              Ask a parent to set up a study plan for you!
            </p>
          </div>
        )}

        {/* Plan cards */}
        {!loading && plans?.length > 0 && (
          <>
            <div className="space-y-4">
              {plans.map((plan, i) => (
                <motion.div
                  key={plan.plan_id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <PlanCard plan={plan} childId={childId} helpEnabled={helpEnabled} />
                </motion.div>
              ))}
            </div>

            {/* Try all plans */}
            {plans.length > 1 && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: plans.length * 0.06 + 0.1 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleTryAll}
                className="mt-5 flex w-full items-center justify-center gap-3 rounded-[1.75rem] bg-foreground py-5 font-display font-black text-white shadow-soft transition hover:brightness-110"
              >
                <ArrowsClockwise size={20} weight="bold" />
                <div className="text-left">
                  <p className="text-base">Try all {plans.length} plans</p>
                  <p className="text-xs font-semibold opacity-70">
                    {new Set(plans.flatMap(p => p.word_ids)).size} words · mixed quiz
                  </p>
                </div>
              </motion.button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
